import { FunctionComponent } from 'react';
import { ContingentCondition } from '@/models/product';
import { Stack, SvgIcon, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { BoldLabel } from '@/components/Layout/Textual';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    ConditionInfoContainer,
    ConditionPreviewContainer,
    DeleteButtonTile,
    IconButtonContainer,
    IconButtonTop,
    SortNumberContainer,
    SplitIconContainer,
} from '@/pageContents/productConfiguration/ProductConfigurationDetail/conditions/ConditionList';

type ContingentConditionPreviewProps = {
    condition: ContingentCondition;
    onCopy: (template: ContingentCondition) => void;
    onUpdate: (condition: ContingentCondition) => void;
    onDelete: (condition: ContingentCondition) => void;
    onPriorityChange: (
        condition: ContingentCondition,
        priorityChange: 'increase' | 'decrease'
    ) => void;
    position: number;
    isLast: boolean;
    isFirst: boolean;
};

export const ContingentConditionPreview: FunctionComponent<
    React.PropsWithChildren<ContingentConditionPreviewProps>
> = ({
    condition,
    onPriorityChange,
    onUpdate,
    onCopy,
    onDelete,
    isFirst,
    isLast,
    position,
}) => {
    return (
        <ConditionPreviewContainer>
            <SortNumberContainer>
                <Typography variant='h6'>{position}.</Typography>
            </SortNumberContainer>
            <ConditionInfoContainer>
                <Stack
                    spacing={2}
                    sx={{
                        width: '50%',
                    }}
                >
                    <Typography>
                        <BoldLabel>Interner Name:&nbsp;</BoldLabel>
                        {condition.name}
                    </Typography>
                    <Typography>
                        <BoldLabel>Kontingent Neu:&nbsp;</BoldLabel>
                        {condition.maxTicketsPerStartDay}
                    </Typography>
                </Stack>
                <Stack
                    spacing={2}
                    sx={{
                        width: '50%',
                    }}
                >
                    {(!!condition.startDateValidFrom ||
                        !!condition.startDateValidTo) && (
                        <Typography>
                            <BoldLabel>Gültigkeitszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {(!!condition.validTo || !!condition.validFrom) && (
                        <Typography>
                            <BoldLabel>Verkaufszeitraum:</BoldLabel>{' '}
                            konfiguriert
                        </Typography>
                    )}

                    {!!condition.personTypeIds.length && (
                        <Typography>
                            <BoldLabel>Personentyp(en):</BoldLabel> konfiguriert
                        </Typography>
                    )}
                </Stack>
            </ConditionInfoContainer>
            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => onUpdate(condition)}
                    disableElevation
                >
                    <EditIcon />
                </IconButtonTop>

                <IconButtonContainer
                    onClick={() => {
                        onCopy({
                            ...condition,
                            id: '',
                            name: `${condition.name} (kopie)`,
                        });
                    }}
                    disableElevation
                >
                    <ContentCopyIcon />
                </IconButtonContainer>
            </SplitIconContainer>

            <SplitIconContainer>
                <IconButtonTop
                    onClick={() => {
                        onPriorityChange(condition, 'increase');
                    }}
                    disabled={isFirst}
                    disableElevation
                >
                    <KeyboardArrowUpIcon />
                </IconButtonTop>
                <IconButtonContainer
                    onClick={() => {
                        onPriorityChange(condition, 'decrease');
                    }}
                    disabled={isLast}
                    disableElevation
                >
                    <KeyboardArrowDownIcon />
                </IconButtonContainer>
            </SplitIconContainer>
            <DeleteButtonTile onClick={() => onDelete(condition)}>
                <SvgIcon>
                    <DeleteIcon />
                </SvgIcon>
            </DeleteButtonTile>
        </ConditionPreviewContainer>
    );
};
